import type { SkinId, TimedModeId, UpgradeId } from "../config/gameplay-config";
import { progressConfig } from "../config/progress-config";
import {
  defaultSelectedSoundtrackId,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";
import {
  defaultArenaBackgroundId,
  type ArenaBackgroundId,
} from "../config/arena-background-config";
import type { RoundSettlementReceipt } from "./round-settlement";
import type { ShareableChallengeIdentity } from "../config/challenge-link-config";

export interface ChallengeRecord {
  readonly bestLevel: number;
  readonly completed: true;
  readonly identity: ShareableChallengeIdentity;
}

export type DailyChallengeRecord = ChallengeRecord;

export interface ProgressPreferences {
  readonly hapticsEnabled: boolean;
  readonly musicEnabled: boolean;
  readonly reducedMotion: boolean;
  readonly soundEnabled: boolean;
}

export interface ProgressStatistics {
  readonly foodCollected: number;
  readonly roundsPlayed: number;
  readonly snakesDefeated: number;
}

export type PersonalBestLevels = Readonly<Record<TimedModeId, number>>;

export interface ProgressState {
  readonly dailyChallengeRecords: readonly ChallengeRecord[];
  readonly firstRunHintDismissed: boolean;
  readonly pendingRoundSettlementReceipts: readonly RoundSettlementReceipt[];
  readonly personalBestLevels: PersonalBestLevels;
  readonly preferences: ProgressPreferences;
  readonly schemaVersion: 3;
  readonly selectedArenaBackgroundId: ArenaBackgroundId;
  readonly selectedSkinId: SkinId;
  readonly selectedSoundtrackId: SelectedSoundtrackId;
  readonly settledRoundReceipts: readonly RoundSettlementReceipt[];
  readonly statistics: ProgressStatistics;
  readonly totalCoins: number;
  readonly unlockedSkinIds: readonly SkinId[];
  readonly upgrades: Readonly<Record<UpgradeId, number>>;
}

export const createDefaultProgressState = (): ProgressState => ({
  dailyChallengeRecords: [],
  firstRunHintDismissed: false,
  pendingRoundSettlementReceipts: [],
  personalBestLevels: {
    "challenge-1": 0,
    "challenge-5": 0,
    standard: 0,
  },
  preferences: {
    hapticsEnabled: true,
    musicEnabled: true,
    reducedMotion: false,
    soundEnabled: true,
  },
  schemaVersion: progressConfig.schemaVersion,
  selectedArenaBackgroundId: defaultArenaBackgroundId,
  selectedSkinId: progressConfig.defaultSkinId,
  selectedSoundtrackId: defaultSelectedSoundtrackId,
  settledRoundReceipts: [],
  statistics: {
    foodCollected: 0,
    roundsPlayed: 0,
    snakesDefeated: 0,
  },
  totalCoins: 0,
  unlockedSkinIds: [progressConfig.defaultSkinId],
  upgrades: {
    "food-value": 0,
    "movement-speed": 0,
    "starting-size": 0,
  },
});

export const cloneProgressState = (state: ProgressState): ProgressState => ({
  dailyChallengeRecords: state.dailyChallengeRecords.map((record) => ({
    ...record,
  })),
  firstRunHintDismissed: state.firstRunHintDismissed,
  pendingRoundSettlementReceipts: state.pendingRoundSettlementReceipts.map(
    (receipt) => ({ ...receipt }),
  ),
  personalBestLevels: { ...state.personalBestLevels },
  preferences: { ...state.preferences },
  schemaVersion: state.schemaVersion,
  selectedArenaBackgroundId: state.selectedArenaBackgroundId,
  selectedSkinId: state.selectedSkinId,
  selectedSoundtrackId: state.selectedSoundtrackId,
  settledRoundReceipts: state.settledRoundReceipts.map((receipt) => ({
    ...receipt,
  })),
  statistics: { ...state.statistics },
  totalCoins: state.totalCoins,
  unlockedSkinIds: [...state.unlockedSkinIds],
  upgrades: { ...state.upgrades },
});
